import { useCallback, useState } from "react"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { PublicMenuThemeDesign, PublicMenuThemeEntity } from "../../Objects/Company/PublicMenuThemeEntity"
import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"
import { useTranslation } from "react-i18next"
import { useGetCompany } from "./useGetCompany"

export const usePublicMenuThemes = () => {
  const { t } = useTranslation("general")

  const { setError } = useError()

  const { enableLoad, disableLoad } = useLoad()

  const [themes, setThemes] = useState<PublicMenuThemeEntity[]>([])

  const { handleGetCompany } = useGetCompany()

  const handleError = useCallback(
    (error: unknown) => {
      setError(t("label_title_erro"), error instanceof Error ? error.message : t("label_unknown_error_description"))
    },
    [setError, t],
  )

  const loadThemes = useCallback(
    async (companyId: number) => {
      try {
        const response = await GetRequest<PublicMenuThemeEntity[]>(
          `${API_ROUTES.COMPANY.PUBLIC_MENU_THEMES}/${companyId}`,
        )
        if (!response.success) throw new Error(response.message)

        setThemes(response.data ?? [])
      } catch (error) {
        handleError(error)
      }
    },
    [handleError],
  )

  const saveTheme = async (companyId: number, name: string, design: PublicMenuThemeDesign, themeId?: number) => {
    try {
      enableLoad()

      const endpoint = `${API_ROUTES.COMPANY.PUBLIC_MENU_THEMES}/${companyId}${themeId ? `/${themeId}` : ""}`

      const response = themeId
        ? await PutRequest<PublicMenuThemeEntity>(endpoint, { name, design })
        : await PostRequest<PublicMenuThemeEntity>(endpoint, { name, design })
      if (!response.success) throw new Error(response.message)

      await loadThemes(companyId)

      await handleGetCompany(() => {})

      return response.data ?? null
    } catch (error) {
      handleError(error)
      return null
    } finally {
      disableLoad()
    }
  }

  return { themes, loadThemes, saveTheme }
}
